import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { useCollection } from "react-firebase-hooks/firestore";

import { useApp } from "../../contexts/AppContext";
import { db } from "../../utils/firebase";

import Button from "../Button/Button";
import Modal from "../Modal/Modal";
import CreateParty from "../CreateOrJoinParty/CreateParty";
import JoinParty from "../CreateOrJoinParty/JoinParty";

type PartyWithMembership = {
  partyUser: PartyUser;
  party?: Party;
  memberCount: number;
};

function MyParties() {
  const {
    state: { user, myPartyUsers, globalUser },
    setBlurBackground,
    setSelectedPartyId,
  } = useApp();

  const [partyDocs, setPartyDocs] = useState<Record<string, Party | undefined>>(
    {}
  );
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [leavingId, setLeavingId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setBlurBackground(true);
  }, [setBlurBackground]);

  useEffect(() => {
    let cancelled = false;
    async function fetchPartyDocs() {
      setLoadingInfo(true);
      const entries = await Promise.all(
        myPartyUsers.map(async (pu) => {
          const partySnap = await getDoc(doc(db, "parties", pu.partyId));
          const party = partySnap.exists()
            ? ({ ...partySnap.data(), _docId: partySnap.id } as Party)
            : undefined;
          return [pu.partyId, party] as const;
        })
      );
      if (!cancelled) {
        setPartyDocs(Object.fromEntries(entries));
        setLoadingInfo(false);
      }
    }
    fetchPartyDocs();
    return () => {
      cancelled = true;
    };
  }, [myPartyUsers]);

  const myPartyIds = useMemo(
    () => myPartyUsers.map((pu) => pu.partyId),
    [myPartyUsers]
  );

  // Firestore `in` rejects empty arrays, so fall back to a sentinel that
  // matches no real partyId — same pattern AppContext uses for similar gated
  // listeners.
  const [allMembersValue] = useCollection(
    query(
      collection(db, "partyUsers"),
      where("active", "==", true),
      where(
        "partyId",
        "in",
        myPartyIds.length > 0 ? myPartyIds.slice(0, 30) : ["foobar"]
      )
    )
  );

  const memberCounts: Record<string, number> = useMemo(() => {
    const counts: Record<string, number> = {};
    if (!allMembersValue) return counts;
    for (const d of allMembersValue.docs) {
      const pid = d.data().partyId as string;
      counts[pid] = (counts[pid] ?? 0) + 1;
    }
    return counts;
  }, [allMembersValue]);

  const partiesInfo: PartyWithMembership[] = useMemo(
    () =>
      myPartyUsers.map((pu) => ({
        partyUser: pu,
        party: partyDocs[pu.partyId],
        memberCount: memberCounts[pu.partyId] ?? 0,
      })),
    [myPartyUsers, partyDocs, memberCounts]
  );

  async function handleLeave(info: PartyWithMembership) {
    if (!user) return;
    setErrorMessage("");
    setLeavingId(info.partyUser._docId);
    try {
      const isCreator = info.party?.createdBy === user.uid;
      if (isCreator) {
        // Re-check member count at action time to avoid races with stale UI.
        const membersSnap = await getDocs(
          query(
            collection(db, "partyUsers"),
            where("partyId", "==", info.partyUser.partyId),
            where("active", "==", true)
          )
        );
        const others = membersSnap.docs.filter(
          (d) => d.data().userId !== user.uid
        );
        if (others.length > 0) {
          setErrorMessage(
            "You can't leave a party you created while others are still in it."
          );
          setLeavingId(null);
          return;
        }
        await deleteDoc(doc(db, "partyUsers", info.partyUser._docId));
        await deleteDoc(doc(db, "parties", info.partyUser.partyId));
      } else {
        await deleteDoc(doc(db, "partyUsers", info.partyUser._docId));
      }
    } catch (err) {
      console.warn("Error leaving party", err);
      setErrorMessage("Something went wrong, please try again.");
    }
    setLeavingId(null);
  }

  return (
    <>
      <div className="flex flex-1 flex-col px-4 py-6 text-white">
        <h2 className="text-3xl font-montserrat text-center mb-6">
          {globalUser?.nickName ? `Hi ${globalUser.nickName}!` : "Your parties"}
        </h2>

        {loadingInfo && partiesInfo.length === 0 && (
          <p className="text-center text-esc-neutral-300">Loading…</p>
        )}

        {!loadingInfo && partiesInfo.length === 0 && (
          <p className="text-center text-esc-neutral-300 mb-6">
            You're not in any parties yet.
          </p>
        )}

        <div className="flex flex-col gap-3 mb-6">
          {partiesInfo.map((info) => {
            const isCreator = info.party?.createdBy === user?.uid;
            const cannotLeave = isCreator && info.memberCount > 1;
            return (
              <div
                key={info.partyUser._docId}
                className="bg-esc-neutral-800 p-4 flex flex-col gap-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-xl font-semibold">
                      {info.party?.name ?? "(unknown party)"}
                    </div>
                    <div className="text-sm text-esc-neutral-300">
                      {info.memberCount}{" "}
                      {info.memberCount === 1 ? "participant" : "participants"}
                      {/* {isCreator && " · you created this party"} */}
                    </div>
                  </div>
                  {info.party && (
                    <div className="text-right text-md">
                      <div className="text-esc-pink tracking-widest">
                        {info.party.shortId}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-between gap-2">
                  <Button
                    variant="secondary"
                    disabled={cannotLeave || leavingId === info.partyUser._docId}
                    spinner={leavingId === info.partyUser._docId}
                    onClick={() => handleLeave(info)}
                  >
                    Leave
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!info.party}
                    onClick={() => setSelectedPartyId(info.partyUser.partyId)}
                  >
                    Enter
                  </Button>
                </div>
                {/* {cannotLeave && (
                  <p className="text-sm text-esc-neutral-300">
                    You can't leave a party you created while others are still
                    in it.
                  </p>
                )} */}
              </div>
            );
          })}
        </div>

        {errorMessage && (
          <div className="text-esc-red-200 text-center mb-4">
            {errorMessage}
          </div>
        )}

        <div className="flex justify-center gap-3 mt-2">
          <Button
            variant="primary"
            size="lg"
            onClick={() => setShowCreate(true)}
            disabled={partiesInfo.length > 5}
          >
            Create party
          </Button>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => setShowJoin(true)}
          >
            Join party
          </Button>
        </div>
      </div>

      {showCreate && (
        <Modal title="Create party" onClose={() => setShowCreate(false)}>
          <CreateParty onCancel={() => setShowCreate(false)} />
        </Modal>
      )}

      {showJoin && (
        <Modal title="Join party" onClose={() => setShowJoin(false)}>
          <JoinParty />
        </Modal>
      )}
    </>
  );
}

export default MyParties;
